import { flattenTree } from './tree-utils';
// Bracket reference: [rowId] where rowId is alphanumeric/underscores
const BRACKET_REF_PATTERN = /\[([a-zA-Z_][a-zA-Z0-9_]*|\d+)\]/g;
// Valid formula characters: brackets, operators, numbers, spaces, parens
const VALID_FORMULA_CHARS = /^[\[\]a-zA-Z0-9_+\-*/() \t.]+$/;
/**
 * Parse a formula string using bracket notation for rowId references.
 * e.g., "[10] + 10 + [Revenue]" = row 10's value + literal 10 + Revenue section total
 *
 * Bracket tokens [xxx] are row references. Bare numbers are literals.
 */
export function parseFormula(formula) {
    const errors = [];
    const references = [];
    if (!formula || formula.trim().length === 0) {
        return { valid: false, references: [], errors: ['Formula cannot be empty'] };
    }
    // Check for balanced parentheses (ignore brackets)
    let parenDepth = 0;
    for (const ch of formula) {
        if (ch === '(')
            parenDepth++;
        if (ch === ')')
            parenDepth--;
        if (parenDepth < 0) {
            errors.push('Unbalanced parentheses');
            break;
        }
    }
    if (parenDepth > 0) {
        errors.push('Unbalanced parentheses');
    }
    // Check for balanced brackets
    let bracketDepth = 0;
    for (const ch of formula) {
        if (ch === '[')
            bracketDepth++;
        if (ch === ']')
            bracketDepth--;
        if (bracketDepth < 0) {
            errors.push('Unbalanced brackets');
            break;
        }
    }
    if (bracketDepth > 0) {
        errors.push('Unbalanced brackets');
    }
    // Check for invalid characters
    if (!VALID_FORMULA_CHARS.test(formula)) {
        const invalid = formula.replace(VALID_FORMULA_CHARS, '');
        errors.push(`Invalid characters: ${invalid}`);
    }
    // Extract bracket references
    const matches = formula.matchAll(BRACKET_REF_PATTERN);
    for (const match of matches) {
        const rowId = match[1];
        references.push({ rowId, raw: match[0] });
    }
    return {
        valid: errors.length === 0,
        references,
        errors,
    };
}
/**
 * Find all nodes whose calculated/subtotal formula references the given rowId.
 */
export function findDependents(nodes, rowId) {
    const flat = flattenTree(nodes);
    const dependents = [];
    for (const node of flat) {
        let formula;
        if (node.data.type === 'calculated') {
            formula = node.data.formula;
        }
        else if (node.data.type === 'subtotal' || node.data.type === 'grand_total') {
            // sumOf contains rowIds
            if (node.data.sumOf.includes(rowId)) {
                dependents.push(node);
                continue;
            }
        }
        if (formula) {
            const parsed = parseFormula(formula);
            if (parsed.references.some((ref) => ref.rowId === rowId)) {
                dependents.push(node);
            }
        }
    }
    return dependents;
}
/**
 * Detect circular references using BFS.
 * Given a node's rowId and its formula, check if following the dependency chain
 * ever leads back to the starting node.
 */
export function hasCircularReference(nodes, fromRowId, formula) {
    const parsed = parseFormula(formula);
    if (!parsed.valid)
        return false;
    const visited = new Set();
    const queue = parsed.references.map((ref) => ref.rowId);
    while (queue.length > 0) {
        const current = queue.shift();
        if (current === fromRowId)
            return true;
        if (visited.has(current))
            continue;
        visited.add(current);
        // Find what this node references
        const flat = flattenTree(nodes);
        const node = flat.find((n) => n.rowId === current);
        if (!node)
            continue;
        if (node.data.type === 'calculated' && node.data.formula) {
            const innerParsed = parseFormula(node.data.formula);
            for (const ref of innerParsed.references) {
                if (!visited.has(ref.rowId)) {
                    queue.push(ref.rowId);
                }
            }
        }
        else if (node.data.type === 'subtotal' || node.data.type === 'grand_total') {
            for (const refId of node.data.sumOf) {
                if (!visited.has(refId)) {
                    queue.push(refId);
                }
            }
        }
    }
    return false;
}
/**
 * Convert a formula using [rowId] bracket references to Excel cell references.
 * e.g., "[10] + 10 + [Revenue]" with rowMap {10->5, Revenue->12} and column "C"
 *        becomes "C5 + 10 + C12"
 *
 * Bracket refs are replaced with cell refs; bare numbers remain as literal values.
 */
export function formulaToExcel(formula, rowIdToExcelRow, columnLetter) {
    return formula.replace(BRACKET_REF_PATTERN, (_match, rowId) => {
        const excelRow = rowIdToExcelRow.get(rowId);
        if (excelRow !== undefined) {
            return `${columnLetter}${excelRow}`;
        }
        // Unknown reference — leave the rowId as-is (will show as error in Excel)
        return rowId;
    });
}
//# sourceMappingURL=formula-utils.js.map